import { createFileRoute, Link } from '@tanstack/react-router'
import { css } from '../../../../../styled-system/css'
import { button } from '../../../../styles/buttonStyles'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { titleWrapper, pageTitle, sectionWrapper, sectionTitle } from '../../../../styles/sectionStyles'
import { textLabel, text } from '../../../../styles/textStyles'
import { table, tableHead, tableHeadCell, tableRow, tableCell } from '../../../../styles/tableStyles'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import Add from '~icons/material-symbols/add.svg'
import KeyboardArrowDown from '~icons/material-symbols/keyboard-arrow-down.svg'
import Edit from '~icons/material-symbols/edit.svg'
import { Select } from '@headlessui/react'
import { useState } from 'react'
import { inputWrapper, inputLabel, requiredFieldLabel, input } from '../../../../styles/inputStyles'
import type { Company } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { useGetMyCompanies, useUpdateCompany } from '../../../../libs/admin/hooks'
import { ValidatableTextInput, ValidatableTextInputWithParser } from '../../../../components/validatable-inputs/text-input'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { bankAccountInfoSchema, basicInfoSchema } from '../-schemas/company-info'
import { encodeBankAccountNumber, encodeBankAccountHolderName } from '../../../../libs/utils/bank-account-encoder'
import { IssueCompanyAccountSection } from './-component/IssueCompanyAccountSection'

export const Route = createFileRoute('/admin/companies/$companyId/')({
  component: RouteComponent,
})

function RouteComponent() {
  return <Layout />
}

const useCompanyData = () => {
  const { companyId } = Route.useParams()
  const { data, isLoading, isFetching, isError } = useGetMyCompanies()
  if (isLoading) {
    return { isLoading }
  }
  if (isError) {
    return { isError }
  }
  if (!companyId) {
    throw new Error('Company ID is missing')
  }
  if (!data) {
    throw new Error('My companies data not found')
  }
  const companyWithStores = data.find((company) => company.company?.id === companyId)
  if (!companyWithStores) {
    if (isFetching) {
      // 新規作成時には事業者情報がない場合があるため、isFetchingでもローディング状態を返す
      return { isLoading: true }
    }
    throw new Error('Company not found')
  }
  return {
    companyId,
    companyWithStores,
    company: companyWithStores.company,
    stores: companyWithStores.stores,
    isLoading,
    isError,
  }
}

const BasicInfo = () => {
  const { company, isLoading, isError } = useCompanyData()
  const [isEditing, setIsEditing] = useState(false)
  const handleSaveClick = () => {
    setIsEditing(false)
  }
  const handleCancelClick = () => {
    setIsEditing(false)
  }
  if (isLoading) {
    return null
  }
  if (isError) {
    console.error("Error Fetching company data")
    return null
  }
  if (company === undefined) {
    return null
  }
  if (isEditing) {
    return (
      <EditBasicInfo
        company={company}
        handleSubmit={handleSaveClick}
        handleCancel={handleCancelClick}
      />
    )
  }
  return (
    <ViewBasicInfo
      company={company}
      handleEditClick={() => setIsEditing(true)}
    />
  )
}

const ViewBasicInfo = ({
  company,
  handleEditClick,
}: {
  company: Company,
  handleEditClick?: () => void,
}) => {
  return (
    <div className={sectionWrapper()}>
      {/* Edit button */}
      <div className={css({
        position: 'absolute',
        top: '16px',
        right: '24px',
        display: 'flex',
      })}>
        <button
          type='button'
          className={button()}
          onClick={handleEditClick}
        >
          <Edit />
          編集
        </button>
      </div>
      <h2 className={sectionTitle()}>基本情報</h2>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <div>
          <div className={textLabel()}>事業者名</div>
          <div className={text()}>{company.name}</div>
        </div>
        <div>
          <div className={textLabel()}>登録代表者名</div>
          <div className={text()}>{company.ownerName}</div>
        </div>
        <div>
          <div className={textLabel()}>住所</div>
          <div className={text()}>{company.address}</div>
        </div>
        <div>
          <div className={textLabel()}>電話番号</div>
          <div className={text()}>{company.contact}</div>
        </div>
      </div>
    </div>
  )
}

const EditBasicInfo = ({
  company,
  handleSubmit,
  handleCancel,
}: {
  company: Company,
  handleSubmit: () => void,
  handleCancel: () => void,
}) => {
  const { mutateAsync } = useUpdateCompany()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companyInfo, setCompanyInfo] = useState<Partial<Company>>({
    name: company.name,
    ownerName: company.ownerName,
    address: company.address,
    contact: company.contact,
  })
  const parseResult = basicInfoSchema.safeParse(companyInfo)
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}
  const shouldDisableSave = isSubmitting || !parseResult.success
  const handleSave = async () => {
    setIsSubmitting(true)
    try {
      await mutateAsync({
        ...companyInfo,
        companyId: company.id,
      })
      setIsSubmitting(false)
      handleSubmit()
    } catch (error) {
      setIsSubmitting(false)
      console.error('Error updating company:', error)
    }
  }

  return (
    <div className={sectionWrapper()}>
      <h2 className={sectionTitle()}>基本情報</h2>
      <ValidatableTextInput
        title='事業者名'
        placeholder='ポケットコーポレーション'
        value={companyInfo.name ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            name: value,
          }))
        }}
        isValid={!fieldErrors.name}
        validationMessage={fieldErrors.name?.[0] ?? 'OK'}
        type='string'
        isRequired
        forceValidation
      />
      <ValidatableTextInput
        title='登録代表者名'
        placeholder='春日太郎'
        value={companyInfo.ownerName ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            ownerName: value,
          }))
        }}
        isValid={!fieldErrors.ownerName}
        validationMessage={fieldErrors.ownerName?.[0] ?? 'OK'}
        type='string'
        isRequired
        forceValidation
      />
      <ValidatableTextInput
        title='住所'
        placeholder='青空県砂浜市浅瀬区1-1-1'
        value={companyInfo.address ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            address: value,
          }))
        }}
        isValid={!fieldErrors.address}
        validationMessage={fieldErrors.address?.[0] ?? 'OK'}
        type='string'
        isRequired
        forceValidation
      />
      <ValidatableTextInput
        title='電話番号'
        placeholder='01-2345-6789'
        value={companyInfo.contact ?? ''}
        onChange={(value) => {
          setCompanyInfo((prev) => ({
            ...prev,
            contact: value,
          }))
        }}
        isValid={!fieldErrors.contact}
        validationMessage={fieldErrors.contact?.[0] ?? 'OK'}
        type='string'
        isRequired
        forceValidation
      />
      <div className={css({
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
      })}>
        <button
          type='button'
          className={button({ color: 'ghost' })}
          onClick={handleCancel}
        >
          キャンセル
        </button>
        <button
          type='submit'
          className={button({ color: 'primary' })}
          onClick={() => handleSave()}
          disabled={shouldDisableSave}
        >
          保存
        </button>
      </div>
    </div>
  )
}

const BankAccountInfo = () => {
  const { company, isLoading, isError } = useCompanyData()
  const [isEditing, setIsEditing] = useState(false)
  if (isLoading) {
    return null
  }
  if (isError) {
    return null
  }
  if (!company) {
    return null
  }

  const handleSaveClick = () => {
    setIsEditing(false)
  }
  const handleCancelClick = () => {
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <EditBankAccountInfo
        company={company}
        handleSubmit={handleSaveClick}
        handleCancel={handleCancelClick}
      />
    )
  }
  return (
    <ViewBankAccountInfo
      company={company}
      handleEditClick={() => setIsEditing(true)}
    />
  )
}

const ViewBankAccountInfo = ({
  company,
  handleEditClick,
}: {
  company: Company,
  handleEditClick?: () => void,
}) => {
  return (
    <div className={sectionWrapper()}>
      {/* Edit button */}
      <div className={css({
        position: 'absolute',
        top: '16px',
        right: '24px',
        display: 'flex',
      })}>
        <button
          type='button'
          className={button()}
          onClick={handleEditClick}
        >
          <Edit />
          編集
        </button>
      </div>
      <h2 className={sectionTitle()}>銀行口座情報</h2>
      <div>
        <div className={textLabel()}>銀行名</div>
        <div className={text()}>{company.bankName}</div>
      </div>
      <div>
        <div className={textLabel()}>金融機関コード</div>
        <div className={text()}>{company.bankCode}</div>
      </div>
      <div>
        <div className={textLabel()}>支店名</div>
        <div className={text()}>{company.bankBranchName}</div>
      </div>
      <div>
        <div className={textLabel()}>支店コード</div>
        <div className={text()}>{company.bankBranchCode}</div>
      </div>
      <div>
        <div className={textLabel()}>口座種別</div>
        <div className={text()}>{company.bankAccountType}</div>
      </div>
      <div>
        <div className={textLabel()}>口座番号</div>
        <div className={text()}>{company.bankAccountNumber}</div>
      </div>
      <div>
        <div className={textLabel()}>口座名義</div>
        <div className={text()}>{company.bankAccountHolderName}</div>
      </div>
    </div>
  )
}

const EditBankAccountInfo = ({
  company,
  handleSubmit,
  handleCancel,
}: {
  company: Company,
  handleSubmit: () => void,
  handleCancel: () => void,
}) => {
  const { mutateAsync } = useUpdateCompany();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<Partial<Company>>({
    bankName: encodeBankAccountHolderName(company.bankName),
    bankCode: encodeBankAccountNumber(company.bankCode),
    bankBranchName: encodeBankAccountHolderName(company.bankBranchName),
    bankBranchCode: encodeBankAccountNumber(company.bankBranchCode),
    bankAccountType: company.bankAccountType,
    bankAccountNumber: encodeBankAccountNumber(company.bankAccountNumber),
    bankAccountHolderName: encodeBankAccountHolderName(company.bankAccountHolderName),
  });

  const parseResult = bankAccountInfoSchema.safeParse(companyInfo);
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}

  const shouldDisableSave = isSubmitting || !parseResult.success;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await mutateAsync({
        ...companyInfo,
        companyId: company.id,
      });
      setIsSubmitting(false);
      handleSubmit();
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error updating bank account info:', error);
    }
  };

  return (
    <div className={sectionWrapper()}>
      <h2 className={sectionTitle()}>銀行口座情報</h2>
      <ValidatableTextInputWithParser
        title='銀行名'
        placeholder='ﾎﾟｹﾂﾄｷﾞﾝｺｳ'
        value={companyInfo.bankName ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankName: value })}
        isValid={!fieldErrors.bankName}
        validationMessage={fieldErrors.bankName?.[0] ?? 'OK'}
        type='string'
        formatter={(value) => value}
        parser={encodeBankAccountHolderName}
        isRequired
        forceValidation
      />
      <ValidatableTextInputWithParser
        title='金融機関コード'
        placeholder='0000'
        value={companyInfo.bankCode ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankCode: value })}
        isValid={!fieldErrors.bankCode}
        validationMessage={fieldErrors.bankCode?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
        forceValidation
      />
      <ValidatableTextInputWithParser
        title='支店名'
        placeholder='ｻｲﾝ'
        value={companyInfo.bankBranchName ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankBranchName: value })}
        isValid={!fieldErrors.bankBranchName}
        validationMessage={fieldErrors.bankBranchName?.[0] ?? 'OK'}
        type='string'
        formatter={(value) => value}
        parser={encodeBankAccountHolderName}
        isRequired
        forceValidation
      />
      <ValidatableTextInputWithParser
        title='支店コード'
        placeholder='000'
        value={companyInfo.bankBranchCode ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankBranchCode: value })}
        isValid={!fieldErrors.bankBranchCode}
        validationMessage={fieldErrors.bankBranchCode?.[0] ?? 'OK'}
        type='string'
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
        forceValidation
      />
      <div className={css(
        inputWrapper.raw(),
        { width: '240px' }
      )}>
        <div className={inputLabel()}>
          口座種別
          <span className={requiredFieldLabel()}>必須</span>
        </div>
        <div className={css({ position: 'relative' })}>
          <Select
            className={css(
              input.raw(),
              { appearance: 'none' }
            )}
            value={companyInfo.bankAccountType}
            onChange={(e) => setCompanyInfo({ ...companyInfo, bankAccountType: e.target.value })}
          >
            <option value={"普通"}>普通</option>
            <option value={"当座"}>当座</option>
          </Select>
          <KeyboardArrowDown className={css({
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          })} />
        </div>
      </div>
      <ValidatableTextInputWithParser
        title='口座番号'
        placeholder='0000000'
        value={companyInfo.bankAccountNumber ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankAccountNumber: value })}
        isValid={!fieldErrors.bankAccountNumber}
        validationMessage={fieldErrors.bankAccountNumber?.[0] ?? 'OK'}
        type='string' 
        parser={encodeBankAccountNumber}
        formatter={(value) => value}
        isRequired
        forceValidation
      />
      <ValidatableTextInputWithParser
        title='口座名義'
        placeholder='ｶｽｶﾞﾀﾛｳ'
        value={companyInfo.bankAccountHolderName ?? ''}
        onChange={(value) => setCompanyInfo({ ...companyInfo, bankAccountHolderName: value })}
        isValid={!fieldErrors.bankAccountHolderName}
        validationMessage={fieldErrors.bankAccountHolderName?.[0] ?? 'OK'}
        type='string'
        formatter={(value) => value}
        parser={encodeBankAccountHolderName}
        isRequired
        forceValidation
      />
      <div className={css({
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
      })}>
        <button
          type='button'
          className={button({ color: 'ghost' })}
          onClick={handleCancel}
        >
          キャンセル
        </button>
        <button
          type='submit'
          className={button({ color: 'primary' })}
          onClick={() => handleSave()}
          disabled={shouldDisableSave}
        >
          保存
        </button>
      </div>
    </div>
  );
};

const StoreList = () => {
  const { companyId, companyWithStores, isLoading, isError } = useCompanyData()
  if (isLoading) {
    return null
  }
  if (isError) {
    return null
  }
  if (!companyWithStores) {
    return null
  }
  const stores = companyWithStores.stores

  return (
    <div className={sectionWrapper()}>
      <div className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      })}>
        <h2 className={sectionTitle()}>店舗一覧</h2>
        <Link
          to="/admin/companies/$companyId/newStores"
          params={{ companyId }}
        >
          <button type='button' className={button({ color: 'primary' })}>
            <Add />
            店舗登録
          </button>
        </Link>
      </div>
      <table className={css(
        table.raw(),
        { borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'border.secondary' }
      )}>
        <thead className={css(
          tableHead.raw(),
          { backgroundColor: 'surface.secondary' }
        )}>
          <tr>
            <th className={tableHeadCell()}>店舗名</th>
            <th className={tableHeadCell()}>登録住所</th>
            <th className={tableHeadCell()} />
          </tr>
        </thead>
        <tbody>
          {stores.map((store) => (
            <tr key={store.id} className={tableRow()}>
              <td className={tableCell()}>{store.name}</td>
              <td className={tableCell()}>{store.fullAddress}</td>
              <td className={tableCell()}>
                <div className={css({
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                })}>
                  <Link
                    to="/admin/companies/$companyId/$storeId"
                    params={{ companyId, storeId: store.id }}
                  >
                    <button type='button' className={button()}>詳細<ArrowForward /></button>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        {/* 事業者情報がない場合 */}
        {stores.length === 0 && (
          <tbody>
            <tr className={tableRow()}>
              <td colSpan={4} className={css({
                textAlign: 'center',
                padding: '16px',
              })}>
                店舗情報がありません
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

const BreadCrumb = () => {
  const { company, isLoading } = useCompanyData()
  if (isLoading) {
    return null
  }
  if (company === undefined) {
    return null
  }
  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link
        to="/admin/companies"
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          事業者一覧
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>{company.name}</span>
    </div>
  )
}

const Layout = () => {
  const companyData = useCompanyData()
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
            <Link to={".."}>
              <ArrowBack />
            </Link>
            事業者詳細
          </div>
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumb />
        <BasicInfo />
        <BankAccountInfo />
        <StoreList />
        <IssueCompanyAccountSection
          companyId={companyData.companyId}
          company={companyData.company}
          isLoading={companyData.isLoading}
          isError={companyData.isError}
        />
      </div>
      {companyData.isLoading && <LoadingOverlay />}
    </div>
  )
}
